import {
  PAYROLL_SETTINGS_ACCESS_PATH,
  PAYROLL_SETTINGS_PATH,
  PAYROLL_SETTINGS_ROSTER_PATH,
} from "./routes";

export type PayrollSettingsPageId = "roster" | "workbook-access";

export interface PayrollSettingsPage {
  id: PayrollSettingsPageId;
  path: string;
}

export interface PayrollSettingsAccessPolicy {
  pages: readonly PayrollSettingsPage[];
  landing: string | null;
  canReach: (pathname: string) => boolean;
}

const PAYROLL_SETTINGS_PAGES = [
  { id: "roster", path: PAYROLL_SETTINGS_ROSTER_PATH },
  { id: "workbook-access", path: PAYROLL_SETTINGS_ACCESS_PATH },
] as const satisfies readonly PayrollSettingsPage[];

const withoutTrailingSlash = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

// Roster is first: someone who can open both lands on the roster. Workbook
// access follows, so access-only people land on the page they can use.
export const payrollSettingsAccessPolicy = ({
  canViewRoster,
  canManageAccess,
}: {
  canViewRoster: boolean;
  canManageAccess: boolean;
}): PayrollSettingsAccessPolicy => {
  const pages = PAYROLL_SETTINGS_PAGES.filter((page) =>
    page.id === "roster" ? canViewRoster : canManageAccess,
  );
  const landing = pages[0]?.path ?? null;
  const reachable = new Set<string>(
    PAYROLL_SETTINGS_PAGES.map((page) => page.path),
  );
  if (landing) reachable.add(PAYROLL_SETTINGS_PATH);

  return {
    pages,
    landing,
    // The index is reachable only when there is a page to land on. The child
    // pages stay reachable either way: each one explains a denial itself, so a
    // direct link is not replaced by a redirect.
    canReach: (pathname) => reachable.has(withoutTrailingSlash(pathname)),
  };
};
